import { clearWorkspaceRoleState, clearWorkspaceRoleStateForOwner, safeGetItem, safeSetItem } from './activeRoleStorage';
import { clearDemoPersonaRefusal } from './demoPersona';
import { clearDemoStageIntroDismissal, clearDemoStageIntroDismissalForOwner } from './demoStageIntro';
import { clearExplainNoteDismissals, clearExplainNoteDismissalsForOwner } from './explainNotes';
import { clearAddWorkTimeOnDrop, clearAddWorkTimeOnDropForOwner } from './addWorkTimeOnDrop';
import { clearAllDraftsForOwner, clearAllDraftsForTenant } from './formDraftStorage';

/**
 * Forget everything this browser remembers ABOUT ONE WORKSPACE.
 *
 * WHY THIS EXISTS. The operator's "Workspace zurücksetzen" (system-admin-api
 * `POST /admin/tenants/:id/reset`) erases every module and puts the onboarding
 * row back to `unset`, so the next person to open the workspace meets the demo /
 * set-up fork. Server-side that reset is total. Browser-side it reached nothing
 * at all — and a browser holds state the reset invalidates:
 *
 *   - the active role id and the task delegation, which point at tenant rows the
 *     wipe deleted, and travel as request headers on the very next call;
 *   - the demo persona mask, likewise;
 *   - the demo bar's first-visit line, dismissed for a demo that no longer
 *     exists — so the explanation never came back for a workspace that is, to
 *     anyone opening it, brand new — and likewise the "Nicht mehr anzeigen" of
 *     the how-this-works notes (issue 994);
 *   - form drafts, still offering to restore input into records that are gone;
 *   - Time's "add working time on a drop, don't ask again" (issue 682), a YES
 *     given to a workspace that no longer exists.
 *
 * SCOPED TO THE SLUG, deliberately. Every module UI shares one origin, so this
 * browser may hold live state for other workspaces of the same user; only the
 * erased one's keys go.
 */
export function forgetWorkspaceLocalState(tenantSlug: string | null | undefined): void {
  const slug = String(tenantSlug || '').trim();
  if (!slug) return;
  clearWorkspaceRoleState(slug);
  clearDemoPersonaRefusal(slug);
  clearDemoStageIntroDismissal(slug);
  clearExplainNoteDismissals(slug);
  clearAddWorkTimeOnDrop(slug);
  clearAllDraftsForTenant(slug);
}

/**
 * Forget what this browser remembers about one workspace FOR ONE ACCOUNT.
 *
 * `forgetWorkspaceLocalState` is for a workspace that no longer exists, so it
 * takes every account's entries with it. A member who merely lost access (a
 * revoked demo guest) is different: the workspace lives on, and another account
 * that used this browser may well still belong to it. Only the entries the stamps
 * attribute to `userId` go — role, delegation, persona, the demo bar's first-visit
 * line, the dismissed how-this-works notes, "don't ask again" and drafts; a
 * colleague's are left exactly as they were.
 * Without a user there is nobody to attribute anything to, so nothing is removed.
 */
export function forgetWorkspaceLocalStateForOwner(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): void {
  const slug = String(tenantSlug || '').trim();
  if (!slug || !userId) return;
  clearWorkspaceRoleStateForOwner(slug, userId);
  clearDemoPersonaRefusal(slug);
  clearDemoStageIntroDismissalForOwner(slug, userId);
  clearExplainNoteDismissalsForOwner(slug, userId);
  clearAddWorkTimeOnDropForOwner(slug, userId);
  clearAllDraftsForOwner(slug, userId);
}

// What this browser last saw the workspace be: past the fork, or at it. Kept per
// workspace and NOT per user — the question it answers is about the workspace,
// and a reset invalidates every account's local state alike.
const PHASE_PREFIX = 'lumiWorkspacePhase:';
const PHASE_FORK = 'fork';
const PHASE_LIVE = 'live';

/**
 * Notice that a workspace has been reset, and drop what this browser remembers
 * about it. Called by `useOnboardingStore` on every status read.
 *
 * A SERVER CANNOT REACH ANOTHER BROWSER'S localStorage, so the purge is driven
 * from what the server does say. Being at the fork (`mode: 'unset'` with
 * `firstRunResolved: false`) is not by itself the signal: a workspace nobody has
 * set up yet sits there too, and purging on the state would fight the role store,
 * which writes an active role as soon as it can resolve one — fork or no fork.
 *
 * The TRANSITION is the signal. A workspace this browser has seen past the fork
 * and now finds back at it has been erased; there is no other way back. The
 * marker lives in localStorage so it survives the reload the reset causes, and
 * a browser that has only ever seen the fork purges nothing.
 *
 * Returns whether it purged, which is what the tests assert on.
 */
export function syncWorkspacePhase(
  tenantSlug: string | null | undefined,
  atFork: boolean,
): boolean {
  const slug = String(tenantSlug || '').trim();
  if (!slug) return false;
  const key = `${PHASE_PREFIX}${slug}`;
  if (!atFork) {
    safeSetItem(key, PHASE_LIVE);
    return false;
  }
  const wasLive = safeGetItem(key) === PHASE_LIVE;
  if (wasLive) forgetWorkspaceLocalState(slug);
  // Written AFTER the purge, which is free to remove anything it recognises.
  safeSetItem(key, PHASE_FORK);
  return wasLive;
}
